/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICartViewProps, IOrderSummaryErrors } from './farmlands-cart';
import { ICartlinesViewProps } from './components/farmlands-cart-line-items';

const _renderCartlines = (
    cartLines: ICartlinesViewProps[] | undefined,
    cartEmptyHeader: string,
    cartEmptyText: string,
    CartlinesWrapper: INodeProps,
    storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean
): JSX.Element[] | JSX.Element => {
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map((cartLine, index) => {
            return (
                <div className='msc-cart-lines-item' key={index}>
                    {cartLine.cartline}
                </div>
            );
        });
    } else {
        return cartDataResult ? (
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart__empty-header'>{cartEmptyHeader}</p>
                <p className='msc-cart__empty-text'>{cartEmptyText}</p>
                {backToShoppingButton}
            </div>
        ) : (
            <div className='msc-cart__waiting-wrapper'>{waitingComponent}</div>
        );
    }
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }

    return (
        <Node {...OrderSummaryItems}>
            {props.promoCode}
            {orderSummaryLines.subtotal}
            {orderSummaryLines.shipping}
            {orderSummaryLines.orderTotal}
            {orderSummaryLines.tax}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            {/* use this if we don't overwrite tax {'GST'} */}
        </Node>
    );
};

const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => {
    const {
        deliveryText,
        clickCollectText,
        cartlines,
        isCartEmpty
    } = props;

    let cartDeliveryMethodMessage: string = '';

    if (!isCartEmpty) {
        // @ts-ignore
        cartDeliveryMethodMessage = cartlines && cartlines[0].data?.cartline.FulfillmentStoreId ? clickCollectText: deliveryText;
    }

    return (
    <>
        {props.continuingShoppingButton}
        {(() => {
            if (props.isUserAuthorised && props.hasCheckedUserAuthority && props.hasEcomPermission) {
                return (
                    <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
                        {/* Cart item section  */}
                        <Node className='msc-cart-section'>
                            {props.title}
                            <Node {...props.CartlinesWrapper}>
                                {
                                    !!cartDeliveryMethodMessage && (
                                        <DeliveryMethodMessage>
                                            {cartDeliveryMethodMessage}
                                        </DeliveryMethodMessage>
                                    )
                                }
                                {_renderCartlines(
                                    cartlines,
                                    props.resources.emptyCartHeader,
                                    props.resources.emptyCartText,
                                    props.CartlinesWrapper,
                                    props.storeSelector,
                                    props.backToShoppingButton,
                                    props.waitingComponent,
                                    props.cartLoadingStatus,
                                    props.cartDataResult,
                                )}
                            </Node>
                            {/* Promotion section */}
                            <Node {...props.promotionBlocksWrapper}>{props.promotionBlocks}</Node>
                        </Node>
                        {/* Order Summary Section */}
                        {props.orderSummaryHeading && (
                            <Node {...props.OrderSummaryWrapper}>
                                {props.deliveryMethodHeading}
                                {props.deliveryMethodOptions}
                                {props.orderSummaryHeading}
                                {props.orderSummaryAccountDetails}
                                {_renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                                {_renderErrorBlock(props.OrderSummaryErrors)}

                                {/* Checkout buttons */}
                                {props.checkoutWithAccountButton}
                                {props.checkoutWithCreditCardButton}
                                {props.checkoutAsSignInUserButton}
                                {props.checkoutAsGuestButton}
                            </Node>
                        )}
                        {props.storeSelector}
                        {props.loadingSpinner}
                    </div>
                );
            } else if (!props.isUserAuthorised && props.hasCheckedUserAuthority) {
                return (
                    <div>
                        We are unable to add this item to your cart due to your current Account status. Please contact us on 0800 200 600
                        for further information.
                    </div>
                );
            } else if (!props.hasEcomPermission) {
                return (
                    <div>
                        We are unable to add this item to your cart due to your current permission on this account. Please contact us on
                        0800 200 600 for further information.
                    </div>
                );
            } else {
                return;
            }
        })()}
    </>
    )
};

const DeliveryMethodMessage = (props: any) => (
    <p className='cart-message'>
        { props.children }
    </p>
)

export default CartView;
